import LinkButton from '@atoms/LinkButton';
import Image from 'next/image';
import Link from 'next/link';
import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';

import Title from '@/components/atoms/Title';
import { SectionWrap } from '@/components/store/components/SectionWrap/SectionWrap';

import {
  COUNTDOWN_DATE,
  DISCOUNT_CODE,
  pricingPlans,
  SHOW_DISCOUNT,
} from '../../config';
import {
  ButtonContent,
  ButtonWrapper,
  CartIcon,
  ComparedAtPrice,
  CopyIcon,
  CountdownItem,
  CountdownLabel,
  CountdownTimer,
  CountdownTitle,
  CountdownValue,
  CountdownWrapper,
  DetailsButton,
  DiscountBadge,
  DiscountCode,
  DiscountCodeBox,
  DiscountCodeWrapper,
  DiscountedPrice,
  DiscountSection,
  FeatureItem,
  FeaturesList,
  ImageWrapper,
  OldPrice,
  PlanDescription,
  PlanName,
  Price,
  PriceAmount,
  PriceNote,
  PriceWrapper,
  PricingCard,
  PricingDisclaimer,
  PricingGrid,
  PricingWrapper,
  SubItem,
  SubitemsList,
  SuccessMessage,
  Switch,
  SwitchWrapper,
} from './Pricing.styled';

export const Pricing = (): ReactElement => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDiscounted, setIsDiscounted] = useState(SHOW_DISCOUNT);
  const [showSuccess, setShowSuccess] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const jsConfettiRef = useRef(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const toggleDetails = (planName: string) => {
    setIsExpanded(prev => {
      if (prev) {
        requestAnimationFrame(() => {
          cardRefs.current[planName]?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        });
      }

      return !prev;
    });
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(DISCOUNT_CODE);

      if (jsConfettiRef.current) {
        jsConfettiRef.current.addConfetti({
          confettiNumber: 50,
        });
      }

      setShowSuccess(true);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  useEffect(() => {
    if (!SHOW_DISCOUNT) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = COUNTDOWN_DATE.getTime();
      const difference = target - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor(
            (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
          ),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000),
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!SHOW_DISCOUNT) return;

    let isMounted = true;

    (async () => {
      const { default: JSConfetti } = await import('js-confetti');
      if (!isMounted) return;
      const instance = new JSConfetti();
      jsConfettiRef.current = instance;
    })();

    return () => {
      isMounted = false;
      if (jsConfettiRef.current?.clearCanvas) {
        jsConfettiRef.current.clearCanvas();
      }
      jsConfettiRef.current = null;
    };
  }, []);

  return (
    <SectionWrap>
      <PricingWrapper>
        <Title>Wybierz pakiet</Title>
        {SHOW_DISCOUNT && (
          <>
            <CountdownWrapper>
              <CountdownTitle>Promocja kończy się za:</CountdownTitle>
              <CountdownTimer>
                <CountdownItem>
                  <CountdownValue>{timeLeft.days}</CountdownValue>
                  <CountdownLabel>Dni</CountdownLabel>
                </CountdownItem>
                <CountdownItem>
                  <CountdownValue>{timeLeft.hours}</CountdownValue>
                  <CountdownLabel>Godziny</CountdownLabel>
                </CountdownItem>
                <CountdownItem>
                  <CountdownValue>{timeLeft.minutes}</CountdownValue>
                  <CountdownLabel>Minuty</CountdownLabel>
                </CountdownItem>
                <CountdownItem>
                  <CountdownValue>{timeLeft.seconds}</CountdownValue>
                  <CountdownLabel>Sekundy</CountdownLabel>
                </CountdownItem>
              </CountdownTimer>
            </CountdownWrapper>
            <DiscountSection>
              <SwitchWrapper>
                <label htmlFor="discount-switch">Zastosuj kod rabatowy</label>
                <Switch isChecked={isDiscounted}>
                  <input
                    type="checkbox"
                    id="discount-switch"
                    checked={isDiscounted}
                    onChange={e => setIsDiscounted(e.target.checked)}
                  />
                  <span />
                </Switch>
              </SwitchWrapper>
              <DiscountCodeWrapper>
                <DiscountCodeBox
                  isVisible={isDiscounted}
                  onClick={handleCopyCode}
                >
                  <DiscountCode>{DISCOUNT_CODE}</DiscountCode>
                  <CopyIcon viewBox="0 0 24 24">
                    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                  </CopyIcon>
                </DiscountCodeBox>
                <SuccessMessage isVisible={showSuccess}>
                  Skopiowano kod! Użyj go przy płatności ❤️
                </SuccessMessage>
              </DiscountCodeWrapper>
            </DiscountSection>
          </>
        )}
        <PricingGrid>
          {pricingPlans.map(plan => {
            const displayPrice = isDiscounted
              ? plan.discountPrice ?? plan.price.toString()
              : plan.price;
            const oldPrice = isDiscounted ? plan.price : undefined;
            const discountPercent = plan.comparedAtPrice
              ? Math.round(
                  ((plan.comparedAtPrice - plan.price) / plan.comparedAtPrice) *
                    100,
                )
              : undefined;

            return (
              <PricingCard
                key={plan.name}
                data-badge={plan.badge}
                highlighted={plan.name === 'Rozszerzony'}
              >
                <div
                  ref={el => {
                    cardRefs.current[plan.name] = el;
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    scrollMarginTop: 16,
                  }}
                />
                <PlanName>{plan.name}</PlanName>
                <PlanDescription>{plan.description}</PlanDescription>
                <Price>
                  <PriceWrapper>
                    {!isDiscounted && plan.comparedAtPrice && (
                      <ComparedAtPrice>
                        {plan.comparedAtPrice} zł
                      </ComparedAtPrice>
                    )}
                    {oldPrice && <OldPrice>{oldPrice} zł</OldPrice>}
                    {isDiscounted ? (
                      <DiscountedPrice>{displayPrice} zł</DiscountedPrice>
                    ) : (
                      <PriceAmount>{displayPrice} zł</PriceAmount>
                    )}
                    {!isDiscounted && discountPercent && (
                      <DiscountBadge>-{discountPercent}%</DiscountBadge>
                    )}
                  </PriceWrapper>
                  {!isDiscounted && plan.comparedAtPrice && (
                    <PriceNote>
                      Najniższa cena z 30 dni przed obniżką:{' '}
                      {plan.comparedAtPrice} zł
                    </PriceNote>
                  )}
                </Price>
                <ImageWrapper>
                  <Image
                    src={plan.image}
                    alt={`Plan ${plan.name}`}
                    fill
                    sizes="(max-width: 992px) 100vw, 50vw"
                    style={{ objectFit: 'contain' }}
                  />
                </ImageWrapper>
                <FeaturesList>
                  {plan.features.map(feature => (
                    <FeatureItem key={feature.name}>
                      <span>
                        <span role="img" aria-label="check">
                          ✅
                        </span>
                        <div>
                          <strong>{feature.name}</strong>
                          {feature.subtitle && <div>{feature.subtitle}</div>}
                        </div>
                      </span>
                      {feature.subitems && isExpanded && (
                        <SubitemsList>
                          {feature.subitems.map(subitem => (
                            <SubItem key={subitem}>{subitem}</SubItem>
                          ))}
                        </SubitemsList>
                      )}
                    </FeatureItem>
                  ))}
                </FeaturesList>
                <ButtonWrapper>
                  <LinkButton link={plan.checkoutUrl}>
                    <ButtonContent>
                      Wybieram
                      <CartIcon viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
                      </CartIcon>
                    </ButtonContent>
                  </LinkButton>
                  <DetailsButton onClick={() => toggleDetails(plan.name)}>
                    {isExpanded ? 'Zwiń szczegóły' : 'Rozwiń szczegóły'}
                  </DetailsButton>
                </ButtonWrapper>
              </PricingCard>
            );
          })}
        </PricingGrid>
      </PricingWrapper>
      <PricingDisclaimer>
        Kupując akceptujesz{' '}
        <Link target="_blank" href="/regulamin">
          regulamin
        </Link>
        .
      </PricingDisclaimer>
    </SectionWrap>
  );
};
